import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { toast } from "sonner";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PATIENT_EDIT } from "@/constants/testIds";

// Same gate as PUT /patients/:pid: require_department("post_sales") — admins
// always, else department post_sales/both (unset = "both", as in core.py) —
// and telecallers never pass the patient access check behind it.
export function canEditPatient(user) {
  if (!user) return false;
  if (["super_admin", "hq_admin"].includes(user.role)) return true;
  if (user.role === "telecaller") return false;
  return ["post_sales", "both"].includes(user.department || "both");
}

// A typed mobile as a 10-digit Indian number, or null if it isn't one.
// "+91 98200 11223" / "09820011223" / "9820011223" → "9820011223".
export function normalizeMobile(value) {
  let d = String(value || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return /^\d{10}$/.test(d) ? d : null;
}

const TEXT_FIELDS = ["name", "email", "gender", "blood_group", "city", "address",
  "pincode", "occupation", "language", "referred_by"];
const GENDERS = ["Female", "Male", "Other"];

export function patientForm(p) {
  const f = {};
  TEXT_FIELDS.forEach((k) => { f[k] = p?.[k] || ""; });
  f.dob = p?.dob ? String(p.dob).slice(0, 10) : "";
  f.age = p?.age != null ? String(p.age) : "";
  f.new_mobile = "";
  return f;
}

/** Only what changed, as the PUT body — or `{ error }` for invalid input.
 * `ageMode` is "dob" or "age", like the walk-in form. The server derives age
 * from dob on read, so an age only sticks once the dob is cleared. */
export function patientChanges(p, form, ageMode) {
  const out = {};
  const name = form.name.trim();
  if (!name) return { error: "Name is required" };
  for (const k of TEXT_FIELDS) {
    const v = form[k].trim();
    if (v !== String(p?.[k] || "").trim()) out[k] = v;
  }
  if (out.email && !/^\S+@\S+\.\S+$/.test(out.email)) return { error: "Enter a valid email" };

  const oldDob = p?.dob ? String(p.dob).slice(0, 10) : "";
  if (ageMode === "dob") {
    if (form.dob !== oldDob) out.dob = form.dob;
  } else if (form.age.trim() !== "") {
    const age = Number(form.age);
    if (!Number.isInteger(age) || age < 0 || age > 130) return { error: "Enter an age between 0 and 130" };
    if (oldDob) { out.dob = ""; out.age = age; }
    else if (age !== (p?.age ?? null)) out.age = age;
  }

  if (form.new_mobile.trim()) {
    const m = normalizeMobile(form.new_mobile);
    if (!m) return { error: "Enter a 10-digit mobile number" };
    if (m !== normalizeMobile(p?.mobile)) out.mobile = m;
  }
  return { changes: out };
}

export default function EditPatientDialog({ patient, open, onClose, onSaved }) {
  const [form, setForm] = useState(() => patientForm(patient));
  const [ageMode, setAgeMode] = useState("age");
  const [bloodGroups, setBloodGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(patientForm(patient));
    setAgeMode(patient?.dob ? "dob" : "age");
    api.get("/master-data", { params: { field: "blood_group" } })
      .then((r) => setBloodGroups((r.data.blood_group || []).map((b) => b.value)))
      .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const derivedAge = useMemo(() => {
    if (!form.dob) return null;
    const b = new Date(form.dob);
    if (Number.isNaN(b.getTime())) return null;
    const t = new Date();
    return t.getFullYear() - b.getFullYear()
      - (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate()) ? 1 : 0);
  }, [form.dob]);

  // Keep a stored value selectable even if it's not in today's option list.
  const genderOptions = form.gender && !GENDERS.includes(form.gender) ? [...GENDERS, form.gender] : GENDERS;
  const bloodOptions = form.blood_group && !bloodGroups.includes(form.blood_group)
    ? [...bloodGroups, form.blood_group] : bloodGroups;

  const save = async () => {
    const { changes, error } = patientChanges(patient, form, ageMode);
    if (error) { toast.error(error); return; }
    if (!Object.keys(changes).length) { onClose(); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/patients/${patient.id}`, changes);
      toast.success("Patient details updated");
      onClose();
      onSaved?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={PATIENT_EDIT.dialog}>
        <DialogHeader><DialogTitle className="font-display text-navy">Edit patient details</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <div className="col-span-2 md:col-span-1">
              <Field label="Name *">
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid={PATIENT_EDIT.nameInput} />
              </Field>
            </div>
            <Field label="Gender">
              <Select value={form.gender || "__none__"} onValueChange={(v) => set("gender", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid={PATIENT_EDIT.genderSelect}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {genderOptions.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setAgeMode("dob")} data-testid={PATIENT_EDIT.ageModeDob}
                  className={`text-xs font-medium ${ageMode === "dob" ? "text-navy underline" : "text-muted-foreground"}`}>DOB</button>
                <span className="text-xs text-muted-foreground">or</span>
                <button type="button" onClick={() => setAgeMode("age")} data-testid={PATIENT_EDIT.ageModeAge}
                  className={`text-xs font-medium ${ageMode === "age" ? "text-navy underline" : "text-muted-foreground"}`}>Age</button>
              </div>
              {ageMode === "dob" ? (
                <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} data-testid={PATIENT_EDIT.dobInput} />
              ) : (
                <Input type="number" min={0} max={130} value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="Years" data-testid={PATIENT_EDIT.ageInput} />
              )}
              {ageMode === "dob" && derivedAge !== null && (
                <p className="text-[11px] text-muted-foreground">{derivedAge} years old</p>
              )}
              {ageMode === "age" && patient?.dob && (
                <p className="text-[11px] text-muted-foreground">Saving an age clears the date of birth.</p>
              )}
            </div>

            <Field label="Blood group">
              <Select value={form.blood_group || "__none__"} onValueChange={(v) => set("blood_group", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid={PATIENT_EDIT.bloodGroupSelect}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unknown</SelectItem>
                  {bloodOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Email">
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid={PATIENT_EDIT.emailInput} />
              </Field>
            </div>
          </div>

          {/* The current number is never shown in full here: revealing it is an
              audited action (View → POST /patients/:id/phone-view). */}
          <div className="grid grid-cols-2 gap-3 rounded-lg bg-cream/40 p-3">
            <Field label="Mobile">
              <p className="flex h-9 items-center text-sm text-ink" data-testid={PATIENT_EDIT.currentMobile}>
                {patient?.mobile ? <>{maskPhone(patient.mobile)} <span className="ml-1 text-xs text-muted-foreground">(current, hidden)</span></> : <span className="text-muted-foreground">None on file</span>}
              </p>
            </Field>
            <Field label="New mobile number">
              <Input type="tel" inputMode="tel" value={form.new_mobile} onChange={(e) => set("new_mobile", e.target.value)}
                placeholder="10-digit mobile" autoComplete="off" data-testid={PATIENT_EDIT.newMobileInput} />
              <p className="text-xs text-muted-foreground">Leave blank to keep the current number.</p>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <div className="col-span-2 md:col-span-3">
              <Field label="Street address">
                <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid={PATIENT_EDIT.addressInput} />
              </Field>
            </div>
            <Field label="City">
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid={PATIENT_EDIT.cityInput} />
            </Field>
            <Field label="Pincode">
              <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} data-testid={PATIENT_EDIT.pincodeInput} />
            </Field>
            <Field label="Occupation">
              <Input value={form.occupation} onChange={(e) => set("occupation", e.target.value)} data-testid={PATIENT_EDIT.occupationInput} />
            </Field>
            <Field label="Language">
              <Input value={form.language} onChange={(e) => set("language", e.target.value)} placeholder="e.g. Marathi" data-testid={PATIENT_EDIT.languageInput} />
            </Field>
            <Field label="Referred by">
              <Input value={form.referred_by} onChange={(e) => set("referred_by", e.target.value)} data-testid={PATIENT_EDIT.referredByInput} />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">
            A new name or mobile is also copied to upcoming appointments and the original lead. The patient ID and branch can't be changed.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid={PATIENT_EDIT.cancelButton}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid={PATIENT_EDIT.saveButton}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
